import type {ReactElement, ReactNode} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {BaseTimelineItem} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/base-timeline-item/base-timeline-item';
import {Text, TextSize} from '@Components/text';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import type {DragDirection, Range, ResizeEndEvent, Span} from '@Libraries/timeline/types';
import {POSTER_MAX_DURATION} from '@PosterWhiteboard/poster/poster.types';
import {TimelineItemType} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/base-timeline-item/base-timeline-item.types';
import {TimelineItemDurationContainerType} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/duration-notification/duration-notification.types';
import {selectItem} from '@Components/poster-editor/poster-editor-reducer';
import type {TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import {MIN_TIME_GAP_FOR_TRANSCRIPT} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type {TranscriptItem} from '@PosterWhiteboard/items/transcript-item/transcript-item';
import {useDndMonitor} from '@dnd-kit/core';
import {
  canItemBeMoveToThisRange,
  getOverlapAndSubtitleIds,
  isOverlapping,
} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/timeline-subtitle-item/timeline-subtitle-item.helper';
import {updateDesignItemSelectionState, updateSubtitleItemResizeData} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';
import {
  findOverlappingPercentageWithRespectToStart,
  getIdsInFrontOfTarget,
} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.helper';
import styles from './timeline-subtitle-item.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {useSingleActivePosterItem} from '@Hooks/poster-editor/useSingleActivePosterItem';

interface TimelineAutoCaptionItemProps {
  id: string;
  range: Range;
  children: ReactNode;
  transcriptId?: string;
  isDisplayOnly?: boolean;
}

export function TimelineSubtitleItem({isDisplayOnly = false, transcriptId = '', ...props}: TimelineAutoCaptionItemProps): ReactElement<any> | null {
  const itemId = props.id;
  const transcriptItem = useAppSelector((state) => selectItem(state, transcriptId)) as TranscriptItemObject | undefined;
  const captionItem = transcriptItem?.subtitlesHashmap[itemId];
  const [rangeWhileResizing, setRangeWhileResizing] = useState(props.range);
  const [rangeWhileDragging, setRangeWhileDragging] = useState(props.range);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const currentPageTime = useAppSelector((state) => {
    return state.posterEditor.currentTime;
  });
  const setCurrentTime = (newValue: number | number[]): void => {
    void window.posterEditor?.whiteboard?.seek(newValue as number);
  };
  const selectedItem = useSingleActivePosterItem();
  const [isDragging, setIsDragging] = useState(false);
  const copyOfTranscript = useRef(transcriptItem);
  const subtitleItemResizeData = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.subtitleItemResizeData;
  });
  const itemDragData = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.itemDragData;
  });
  const [isBeingMovedDueToDrag, setIsBeingMovedDueToDrag] = useState(false);
  const [rangeWhileisBeingMovedDueToDrag, setRangeWhileisBeingMovedDueToDrag] = useState(props.range);
  const dispatch = useAppDispatch();

  useEffect((): void => {
    setRangeWhileDragging(props.range);
  }, [props.range]);

  useDndMonitor({
    onDragStart(e) {
      const id = e.active.id as string;
      if (id.split('_')[0] === props.id) {
        setIsDragging(true);
      }
    },
    onDragMove(e) {
      const dragElement = e.active.data.current;
      const updatedRange = dragElement?.getSpanFromDragEvent?.(e) as Range;
      if (updatedRange) {
        setRangeWhileDragging(updatedRange);
      }

      // disabled for now
      // setPotentialMovementDueToDrag();
    },
    onDragEnd(e) {
      const id = e.active.id as string;
      if (id.split('_')[0] === props.id) {
        setIsDragging(false);
      }
      setIsBeingMovedDueToDrag(false);
    },
  });

  if (!captionItem || !transcriptItem) {
    return null;
  }

  const getTimeInSeconds = (): number => {
    return props.range.end - props.range.start;
  };

  const onResizeStart = (): void => {
    copyOfTranscript.current = transcriptItem;
    setRangeWhileResizing(props.range);
    setIsResizing(true);
  };

  const validateUpdatedRange = (updatedRange: Span, direction: DragDirection): boolean => {
    const transcriptItem2 = window.posterEditor?.whiteboard?.getItemForId(transcriptItem.uid) as TranscriptItem;
    const pageStartTime = transcriptItem2.getPageStartTime();

    if (direction === 'start' && updatedRange.start < pageStartTime) {
      return false;
    }
    if (direction === 'end' && updatedRange.end > POSTER_MAX_DURATION) {
      return false;
    }

    return canItemBeMoveToThisRange(updatedRange, direction, itemId, transcriptItem2);
  };

  const onAutoScroll = (increment: number, direction: DragDirection = 'end'): void => {
    const subtitleTranscriptItem = window.posterEditor?.whiteboard?.getItemForId(transcriptId) as TranscriptItem;
    const subtitleItem = subtitleTranscriptItem.subtitlesHashmap[itemId];
    if (!subtitleItem || !subtitleTranscriptItem) {
      return;
    }

    if (direction === 'start') {
      setRangeWhileResizing((oldRange) => {
        const updatedRange: Range = {
          start: oldRange.start + increment,
          end: oldRange.end,
        };
        if (!validateUpdatedRange(updatedRange, direction)) {
          return oldRange;
        }
        const overlappingIds = getOverlapAndSubtitleIds(updatedRange, itemId, direction, transcriptItem);
        if (overlappingIds) {
          dispatch(updateSubtitleItemResizeData(overlappingIds));
        }
        return updatedRange;
      });
      return;
    }
    setRangeWhileResizing((oldRange) => {
      const updatedRange: Range = {
        start: oldRange.start,
        end: oldRange.end + increment,
      };
      if (!validateUpdatedRange(updatedRange, direction)) {
        return oldRange;
      }
      const overlappingIds = getOverlapAndSubtitleIds(updatedRange, itemId, direction, transcriptItem);
      if (overlappingIds) {
        dispatch(updateSubtitleItemResizeData(overlappingIds));
      }
      return updatedRange;
    });
  };

  const onResizeMove = (updatedRange: Span, direction: DragDirection): void => {
    if (!validateUpdatedRange(updatedRange, direction)) {
      return;
    }

    const subtitleTranscriptItem = window.posterEditor?.whiteboard?.getItemForId(transcriptId) as TranscriptItem;
    const subtitleItem = subtitleTranscriptItem.subtitlesHashmap[itemId];
    if (!subtitleItem || !subtitleTranscriptItem) {
      return;
    }

    const overlappingIds = getOverlapAndSubtitleIds(updatedRange, itemId, direction, transcriptItem);
    if (overlappingIds) {
      dispatch(updateSubtitleItemResizeData(overlappingIds));
    }
    setRangeWhileResizing(updatedRange);
  };

  const updateStartTime = async (startTime: number): Promise<void> => {
    const subtitleTranscriptItem = window.posterEditor?.whiteboard?.getItemForId(transcriptId) as TranscriptItem;
    const subtitleItem = subtitleTranscriptItem.subtitlesHashmap[itemId];

    if (!subtitleItem) {
      return;
    }

    await subtitleItem.updateFromObject({
      startTime: startTime - subtitleTranscriptItem.page.getStartTime(),
      hasUserEdited: true,
    });
  };

  const updateEndTime = async (endTime: number): Promise<void> => {
    const subtitleTranscriptItem = window.posterEditor?.whiteboard?.getItemForId(transcriptId) as TranscriptItem;
    const subtitleItem = subtitleTranscriptItem.subtitlesHashmap[itemId];

    if (!subtitleItem) {
      return;
    }

    await subtitleItem.updateFromObject({
      endTime: endTime - subtitleTranscriptItem.page.getStartTime(),
      hasUserEdited: true,
    });
  };

  const updateRangeForSubtitle = async (updatedRange: Range, direction: DragDirection): Promise<void> => {
    const posterDuration = window.posterEditor?.whiteboard?.getDuration();
    if (!posterDuration) {
      return;
    }

    if (direction === 'start') {
      let updatedStartTime = updatedRange.start < 0 ? 0 : updatedRange.start;
      if (updatedRange.end - updatedStartTime < MIN_TIME_GAP_FOR_TRANSCRIPT) {
        updatedStartTime = updatedRange.end - MIN_TIME_GAP_FOR_TRANSCRIPT;
      }

      await updateStartTime(updatedStartTime);
    } else {
      let updatedEndTime = updatedRange.end > posterDuration ? posterDuration : updatedRange.end;
      if (updatedEndTime - updatedRange.start < MIN_TIME_GAP_FOR_TRANSCRIPT) {
        updatedEndTime = updatedRange.start + MIN_TIME_GAP_FOR_TRANSCRIPT;
      }

      await updateEndTime(updatedEndTime);
    }

    const subtitleTranscriptItem = window.posterEditor?.whiteboard?.getItemForId(transcriptId) as TranscriptItem;
    if (!subtitleTranscriptItem) {
      return;
    }

    if (subtitleItemResizeData) {
      await subtitleTranscriptItem.updateOverlappingSubtitles(subtitleItemResizeData);
      const newDuration = Math.min(subtitleTranscriptItem.getEndTimeOfLastItem(), POSTER_MAX_DURATION);
      if (newDuration > posterDuration && newDuration <= POSTER_MAX_DURATION) {
        await subtitleTranscriptItem.page.updateFromObject(
          {
            duration: newDuration,
          },
          {undoable: false}
        );
      } else {
        await subtitleTranscriptItem.trimSubtitlesExceedingDuration(POSTER_MAX_DURATION);
      }
    }
  };

  const onResizeEnd = async (event: ResizeEndEvent): Promise<void> => {
    const updatedRange = event.active.data.current.getSpanFromResizeEvent?.(event);
    if (!updatedRange) {
      return;
    }

    setIsUpdating(true);
    await updateRangeForSubtitle(updatedRange, event.direction);
    dispatch(updateSubtitleItemResizeData(null));
    setIsUpdating(false);
    setTimeout((): void => {
      setIsResizing(false);
    }, 200);
  };

  const onClickItem = (): void => {
    if (getSelectedState()) {
      return;
    }

    if (isResizing || isDragging) {
      return;
    }

    const transcript = window.posterEditor?.whiteboard?.getItemForId(transcriptId);
    if (!transcript) {
      return;
    }

    transcript.page.clearSelection();
    transcript.page.activeSelection.selectFabricObjects([transcript.fabricObject]);
    dispatch(updateDesignItemSelectionState(false));

    const range = getRange();
    if (currentPageTime < range.start || currentPageTime > range.end) {
      setCurrentTime(range.start);
    }
  };

  const getContent = (): ReactElement<any> => {
    return (
      <div className={`${styles.contentContainer} flex-h-row flex-align-items-center`}>
        <Icon icon={captionItem.hasUserEdited ? 'icon-captions' : 'icon-ai-subtitles'} size={IconSize.SIZE_ICON_16} className={`${styles.icon} spacing-m-r-2`} />
        <Text className={styles.text} val={captionItem.text} size={TextSize.XXSMALL} />
      </div>
    );
  };

  const getSelectedState = (): boolean => {
    if (selectedItem && selectedItem.uid === transcriptId) {
      return currentPageTime >= props.range.start && currentPageTime <= props.range.end;
    }
    return false;
  };

  const getRange = (): Range => {
    if (isDragging) {
      return rangeWhileDragging;
    }

    // disabled for now
    // if (isBeingMovedDueToDrag) {
    //   return rangeWhileisBeingMovedDueToDrag;
    // }

    if (subtitleItemResizeData && subtitleItemResizeData.ids.includes(itemId) && !isResizing) {
      return {
        start: props.range.start + subtitleItemResizeData.overlap + (subtitleItemResizeData.direction === 'end' ? MIN_TIME_GAP_FOR_TRANSCRIPT : -MIN_TIME_GAP_FOR_TRANSCRIPT),
        end: props.range.end + subtitleItemResizeData.overlap + (subtitleItemResizeData.direction === 'end' ? MIN_TIME_GAP_FOR_TRANSCRIPT : -MIN_TIME_GAP_FOR_TRANSCRIPT),
      };
    }

    if (isResizing || isUpdating) {
      return rangeWhileResizing;
    }

    return props.range;
  };

  const setPotentialMovementDueToDrag = (): void => {
    if (!itemDragData) {
      return;
    }
    if (isDragging) {
      return;
    }

    if (iAmBeingPushed(itemDragData.dragRange, itemDragData.id)) {
      setIsBeingMovedDueToDrag(true);
    } else {
      setIsBeingMovedDueToDrag(false);
    }
  };

  const iAmBeingPushed = (rangeOfDraggingItem: Range, idOfDraggingItem?: string): boolean => {
    const sortedIds: [string, number, number][] = [];
    for (const [key, item] of Object.entries(transcriptItem.subtitlesHashmap)) {
      if (key !== idOfDraggingItem) {
        sortedIds.push([key, item.startTime, item.endTime]);
      }
    }
    sortedIds.sort((a, b) => {
      return a[1] - b[1];
    });

    let overlap = false;
    const overlapIds: string[] = [];
    for (const eachItem of sortedIds) {
      if (isOverlapping(rangeOfDraggingItem, eachItem[1], eachItem[2])) {
        overlapIds.push(eachItem[0]);
        overlap = true;
      }
    }

    if (!overlap || overlapIds.length < 1) {
      return false;
    }

    // otherwise check the first overlapping item
    const firstOverlappingItem = transcriptItem.subtitlesHashmap[overlapIds[0]];
    const overlapPercentage = findOverlappingPercentageWithRespectToStart(firstOverlappingItem.startTime, firstOverlappingItem.endTime, rangeOfDraggingItem);
    const duration = rangeOfDraggingItem.end - rangeOfDraggingItem.start;

    // this is needed to be handled
    if (overlapPercentage < 50) {
      const subtitleNewEndTime = firstOverlappingItem.startTime - MIN_TIME_GAP_FOR_TRANSCRIPT;
      const subtitleNewStartTime = subtitleNewEndTime - duration;
      if (subtitleNewStartTime <= 0 || subtitleNewEndTime < 0) {
        return false;
      }

      const itemBehindIndex =
        sortedIds
          .map((item) => {
            return item[0];
          })
          .indexOf(overlapIds[0]) - 1;

      if (
        itemBehindIndex >= 0 &&
        transcriptItem.subtitlesHashmap[sortedIds[itemBehindIndex][0]] &&
        isOverlapping(
          {
            start: subtitleNewStartTime,
            end: subtitleNewEndTime,
          },
          transcriptItem.subtitlesHashmap[sortedIds[itemBehindIndex][0]].startTime,
          transcriptItem.subtitlesHashmap[sortedIds[itemBehindIndex][0]].endTime
        )
      ) {
        const itemBehindId = sortedIds[itemBehindIndex][0];

        const subtitleIdsThatNeedEditing = getIdsInFrontOfTarget(
          sortedIds.map((item) => {
            return item[0];
          }),
          itemBehindId
        );

        // if in subtitleIdsThatNeedEditing then move otherwise off
        if (subtitleIdsThatNeedEditing.includes(props.id)) {
          setRangeWhileisBeingMovedDueToDrag({
            start: props.range.start + duration,
            end: props.range.end + duration,
          });
          return true;
        }
        return false;
      }

      return false;
    }

    // check if items are moving forward
    const subtitleIdsThatNeedEditing = getIdsInFrontOfTarget(
      sortedIds.map((item) => {
        return item[0];
      }),
      overlapIds[0]
    );
    if (!subtitleIdsThatNeedEditing || subtitleIdsThatNeedEditing.length === 0) {
      return false;
    }

    if (
      isOverlapping(
        {
          start: firstOverlappingItem.endTime + MIN_TIME_GAP_FOR_TRANSCRIPT,
          end: firstOverlappingItem.endTime + MIN_TIME_GAP_FOR_TRANSCRIPT + duration,
        },
        transcriptItem.subtitlesHashmap[subtitleIdsThatNeedEditing[0]].startTime,
        transcriptItem.subtitlesHashmap[subtitleIdsThatNeedEditing[0]].endTime
      ) &&
      overlapIds.includes(itemId) &&
      overlapIds[0] !== itemId
    ) {
      const overlappingValue = transcriptItem.subtitlesHashmap[subtitleIdsThatNeedEditing[0]].startTime - firstOverlappingItem.endTime + MIN_TIME_GAP_FOR_TRANSCRIPT + duration;
      setRangeWhileisBeingMovedDueToDrag({
        start: props.range.start + overlappingValue,
        end: props.range.end + overlappingValue,
      });

      return true;
    }

    return false;
  };

  const isLastItem = (): boolean => {
    const transcriptItem = window.posterEditor?.whiteboard?.getItemForId(transcriptId) as TranscriptItem | undefined;
    if (!transcriptItem) {
      return false;
    }
    const idsInOrder = transcriptItem.getSubtitleIdsInOrder();
    return idsInOrder.indexOf(itemId) === idsInOrder.length - 1;
  };

  const showAddButton = (): boolean => {
    return isLastItem() && !!selectedItem && selectedItem.uid === transcriptId;
  };

  const onClickAddButton = (): void => {
    const transcriptItem = window.posterEditor?.whiteboard?.getItemForId(transcriptId) as TranscriptItem | undefined;
    void transcriptItem?.addSubtitleAfterId(itemId);
  };

  const isAddButtonDisabled = (): boolean => {
    const transcriptItem = window.posterEditor?.whiteboard?.getItemForId(transcriptId) as TranscriptItem | undefined;
    if (transcriptItem) {
      return transcriptItem.isAddAfterSubtitleDisabled(itemId);
    }

    return true;
  };

  return (
    <BaseTimelineItem
      isHidden={isDisplayOnly}
      id={`${itemId}_${transcriptId ?? '-'}`}
      key={`${itemId}_${transcriptId ?? '-'}`}
      range={getRange()}
      showAddButton={showAddButton()}
      isAddButtonDisabled={isAddButtonDisabled()}
      duration={getTimeInSeconds()}
      onResizeStart={onResizeStart}
      onResizeMove={(e, direction): void => {
        onResizeMove(e, direction);
      }}
      onResizeEnd={(e): void => {
        void onResizeEnd(e);
      }}
      durationItemType={TimelineItemDurationContainerType.Green}
      onAutoScroll={onAutoScroll}
      onClick={onClickItem}
      minDuration={MIN_TIME_GAP_FOR_TRANSCRIPT}
      maxDuration={POSTER_MAX_DURATION}
      isSelected={getSelectedState()}
      type={TimelineItemType.Green}
      posterDuration={window.posterEditor?.whiteboard?.getCurrentPage().getDuration()}
      onClickAddButton={onClickAddButton}
    >
      {getContent()}
    </BaseTimelineItem>
  );
}
